import React, { useState, useRef, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { ArrowLeft, Plus, Download, Trash2, Search, Building2 } from "lucide-react";
import { Constants } from "../../../utils/constants";
import toastUtils from "../../../utils/toast";
import { showApiMessage } from "../../../utils/messageHandler";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import billingService from "../../../services/billingService";
import * as localService from "../../../services/localService";
import { getCurrentUserOrganizationId } from "../host-clients/utils/hostClientsUtils";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HHeader from "../../../hcomponents/HHeader";
import { Button } from "../../../shadcn/ui/button";
import { organizationApiService } from "../../../services/apiV2Service";
import { useCurrencies } from "../../../hooks/useCurrencies";
import { useUserCurrency } from "../../../hooks/useUserCurrency";
import logger from "../../../utils/logger";


const AddInvoice = ({ onBack }) => {
  const history = useHistory();
  const previewRef = useRef();

  // Check if user is HOST_PROPERTY_MANAGEMENT
  const currentUser = localService.getUser();
  const isHostPropertyManagement = currentUser?.organization?.type?.code === "HOST_PROPERTY_MANAGEMENT";

  // Load currencies and user currency
  const { currencies, currenciesLoading, currenciesError } = useCurrencies();
  const userCurrencyCode = useUserCurrency();

  // Redirect if user doesn't have permission
  useEffect(() => {
    if (!isHostPropertyManagement) {
      toastUtils.error("Access Denied", {
        description: "Only property management users can create invoices.",
      });
      history.push("/host/invoice");
    }
  }, [isHostPropertyManagement, history]);

  // Invoice form state - only fields needed for API
  const [invoiceData, setInvoiceData] = useState({
    description: "",
    currencyId: null, // Will be set when currencies load
  });

  const [invoiceItems, setInvoiceItems] = useState([]);
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Organization search state
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const [orgSearchResults, setOrgSearchResults] = useState([]);
  const [isSearchingOrgs, setIsSearchingOrgs] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [showOrgResults, setShowOrgResults] = useState(false);

  // Load templates and initialize form
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        // Set default template data
        setTemplates({
          defaultCompany: {
            name: "Spacia Workspace Solutions",
            email: "billing@spacia.page",
            phone: "+1 (555) 123-4567",
            bankDetails: "Spacia Bank",
            iban: "US12-3456-7890-1234",
            address: "456 Innovation Drive, Tech City, TC 12345",
          },
          defaultTerms:
            "Payment due within 30 days. Late payments may incur additional charges. All amounts are in KES.",
        });
        setLoading(false);
      } catch (error) {
        logger.error("Error loading templates:", error);
        setLoading(false);
      }
    };

    loadTemplates();
  }, []);

  // Set default currency based on user's country when currencies load
  useEffect(() => {
    if (currencies.length > 0 && !invoiceData.currencyId && userCurrencyCode) {
      const userCurrency = currencies.find(c => c.code === userCurrencyCode);
      if (userCurrency) {
        setInvoiceData(prev => {
          // Only update if currencyId is not already set
          if (prev.currencyId) return prev;
          return {
            ...prev,
            currencyId: userCurrency.id,
          };
        });
      }
    }
  }, [currencies, invoiceData.currencyId, userCurrencyCode]);

  const searchOrganizations = useCallback(async () => {
    if (!orgSearchTerm.trim()) return;

    setIsSearchingOrgs(true);
    try {
      const organizationId =
        getCurrentUserOrganizationId() || currentUser?.organizationId || currentUser?.organization?.id;
      const response = await organizationApiService.searchOrganizations({
        q: orgSearchTerm.trim(),
        page: 1,
        limit: 10,
        parentId: organizationId,
      });

      if (response.data?.data) {
        // Filter out current user's org and Individual Customer orgs (typeId 7 / HOST_INDIVIDUAL_CUSTOMER)
        const filteredResults = response.data.data.filter(org => {
          const isCurrentOrg = String(org.id) === String(organizationId);
          const isIndividualCustomer = org.typeId === 7 || org.type?.code === "HOST_INDIVIDUAL_CUSTOMER";
          return !isCurrentOrg && !isIndividualCustomer;
        });
        setOrgSearchResults(filteredResults);
        setShowOrgResults(true);
      }
    } catch (error) {
      logger.error("Error searching organizations:", error);
      setOrgSearchResults([]);
    } finally {
      setIsSearchingOrgs(false);
    }
  }, [orgSearchTerm]);

  // Debounced organization search
  useEffect(() => {
    if (!orgSearchTerm.trim()) {
      setOrgSearchResults([]);
      setShowOrgResults(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchOrganizations();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [orgSearchTerm, searchOrganizations]);

  const handleSelectOrganization = org => {
    if (!org) {
      // Clear selection
      setSelectedOrganization(null);
      setOrgSearchTerm("");
      return;
    }

    setSelectedOrganization(org);
    setOrgSearchTerm(org.name);
    setShowOrgResults(false);
    // Clear search results to prevent dropdown from reappearing
    setOrgSearchResults([]);
  };

  // Calculate totals
  const subtotal = invoiceItems.reduce((sum, item) => sum + item.amount, 0);
  const total = subtotal;

  // Update item amount when unitPrice or quantity change
  const updateItem = (id, field, value) => {
    setInvoiceItems(prev =>
      prev.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item };

          // Parse numeric fields
          if (field === "unitPrice" || field === "quantity" || field === "amount") {
            updatedItem[field] = parseFloat(value) || 0;
            // Only auto-calculate if unitPrice or quantity changed, not amount
            if (field === "unitPrice" || field === "quantity") {
              updatedItem.amount = updatedItem.unitPrice * updatedItem.quantity;
            }
          } else {
            // For description and other text fields, keep as string
            updatedItem[field] = value;
          }

          return updatedItem;
        }
        return item;
      })
    );
  };

  // Add new item
  const addItem = () => {
    const newId = invoiceItems.length > 0 ? Math.max(...invoiceItems.map(item => item.id)) + 1 : 1;
    setInvoiceItems(prev => [
      ...prev,
      {
        id: newId,
        description: "",
        unitPrice: 0,
        quantity: 0,
        amount: 0,
      },
    ]);
  };

  // Remove item
  const removeItem = id => {
    setInvoiceItems(prev => prev.filter(item => item.id !== id));
  };

  // Update invoice data function
  const updateInvoiceData = (field, value) => {
    setInvoiceData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Download PDF function
  const handleDownloadPDF = async () => {
    try {
      const element = previewRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
        width: element.scrollWidth,
        height: element.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // Add padding to the PDF
      const pdfWidth = 210; // A4 width in mm
      const pdfHeight = 297; // A4 height in mm
      const margin = 20; // 20mm margin on all sides
      const contentWidth = pdfWidth - margin * 2;
      const contentHeight = pdfHeight - margin * 2;

      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = margin; // Start with top margin

      // Add first page with margins
      pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
      heightLeft -= contentHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight + margin;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
        heightLeft -= contentHeight;
      }

      pdf.save(`invoice-${invoiceData.invoiceNumber || "draft"}.pdf`);

      toastUtils.success("PDF downloaded successfully!", {
        description: "Your invoice has been saved as PDF.",
      });
    } catch (error) {
      logger.error("Error generating PDF:", error);
      toastUtils.error("Failed to generate PDF", {
        description: "Please try again.",
      });
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      history.push("/host/invoice");
    }
  };

  const handleCreateInvoice = async () => {
    try {
      setIsSubmitting(true);
      if (!selectedOrganization) {
        toastUtils.error("Please select a client organization");
        return;
      }

      if (!invoiceItems || invoiceItems.length === 0) {
        toastUtils.error("Please add at least one invoice item");
        return;
      }

      const invoicePayload = {
        organizationId: selectedOrganization.id,
        clientReference: `${selectedOrganization.id}00${uuidv4().replace(/-/g, "")}`,
        description: invoiceData.description || `Invoice for ${selectedOrganization.name}`,

        currencyId: invoiceData.currencyId,
        invoiceItems: invoiceItems.map(item => ({
          amount: item.amount.toFixed(2),
          unitPrice: item.unitPrice.toFixed(2),
          quantity: item.quantity,
          description: item.description,
        })),
        productType: "DIRECT_BOOKING",
        serviceDate: new Date().toISOString().split("T")[0],
        providerId: getCurrentUserOrganizationId(),
      };

      logger.log("📦 Invoice payload:", invoicePayload);

      const response = await billingService.createInvoice(invoicePayload);

      if (response.status === 201) {
        showApiMessage(response, "create invoice");
        setTimeout(() => {
          handleBack();
        }, 1500);
      }
    } catch (error) {
      logger.error("❌ Error creating invoice:", error);
      showApiMessage(error, "create invoice");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <div className="space-y-4 animate-pulse">
          <div className="w-64 h-8 rounded bg-slate-200"></div>
          <div className="w-full h-96 rounded bg-slate-200"></div>
        </div>
      </div>
    );
  }

  return (
    <HPageContainer>
      <HHeader
        rightButton={
          <Button variant="ghost" onClick={handleBack} className="flex gap-2 items-center">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        }
      />
      <div className="overflow-y-auto flex-1 p-3 sm:p-4 md:p-6 lg:p-8">
        <div className="flex flex-col gap-4 sm:flex-row">
          <InvoiceForm
            invoiceData={invoiceData}
            updateInvoiceData={updateInvoiceData}
            invoiceItems={invoiceItems}
            updateItem={updateItem}
            addItem={addItem}
            removeItem={removeItem}
            onCreateInvoice={handleCreateInvoice}
            isSubmitting={isSubmitting}
            onBack={handleBack}
            templates={templates}
            orgSearchTerm={orgSearchTerm}
            setOrgSearchTerm={setOrgSearchTerm}
            orgSearchResults={orgSearchResults}
            isSearchingOrgs={isSearchingOrgs}
            showOrgResults={showOrgResults}
            setShowOrgResults={setShowOrgResults}
            selectedOrganization={selectedOrganization}
            onSelectOrganization={handleSelectOrganization}
            currencies={currencies}
            currenciesLoading={currenciesLoading}
            currenciesError={currenciesError}
            selectedCurrencyId={invoiceData.currencyId}
            onCurrencyChange={currencyId => updateInvoiceData("currencyId", currencyId)}
          />
          <InvoicePreview
            ref={previewRef}
            invoiceData={invoiceData}
            invoiceItems={invoiceItems}
            total={total}
            onDownloadPDF={handleDownloadPDF}
            selectedOrganization={selectedOrganization}
            selectedCurrency={currencies.find(c => c.id === invoiceData.currencyId)}
          />
        </div>
      </div>
    </HPageContainer>
  );
};

// Left Form Component
const InvoiceForm = ({
  invoiceData,
  updateInvoiceData,
  invoiceItems,
  updateItem,
  addItem,
  removeItem,
  onCreateInvoice,
  isSubmitting,
  onBack,
  templates,
  orgSearchTerm,
  setOrgSearchTerm,
  orgSearchResults,
  isSearchingOrgs,
  showOrgResults,
  setShowOrgResults,
  selectedOrganization,
  onSelectOrganization,
  currencies,
  currenciesLoading,
  currenciesError,
  selectedCurrencyId,
  onCurrencyChange,
}) => {
  const selectedCurrency = currencies.find(c => c.id === selectedCurrencyId);
  const userCurrencyCode = useUserCurrency();
  // Use currency code (KES, GHS) instead of symbol for display
  const currencyDisplay = selectedCurrency?.code || userCurrencyCode || "KES";

  return (
    <div className="overflow-y-auto w-full max-h-screen bg-white rounded-2xl md:w-1/2 shadow-xs invoice-scrollbar">
      <div className="p-4 lg:p-6">
        {/* Client Organization */}
        <div className="mb-6">
          <h3 className="mb-3 text-base font-medium text-gray-900">Client Information</h3>
          <div className="space-y-3">
            {/* Organization Search */}
            <div className="relative">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                <Building2 className="w-3.5 h-3.5 inline mr-1" />
                Search Organization
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={orgSearchTerm}
                  onChange={e => setOrgSearchTerm(e.target.value)}
                  onFocus={() => {
                    if (orgSearchResults.length > 0) setShowOrgResults(true);
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowOrgResults(false), 200);
                  }}
                  className="px-3 py-2 pr-10 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search for client organization..."
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {isSearchingOrgs ? (
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Search className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Search Results Dropdown */}
              {showOrgResults && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {isSearchingOrgs ? (
                    <div className="px-3 py-2 space-y-2">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="flex items-center space-x-2 animate-pulse">
                          <div className="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-full"></div>
                          <div className="flex-1 space-y-1">
                            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : orgSearchResults.length > 0 ? (
                    orgSearchResults.map(org => (
                      <button
                        key={org.id}
                        onClick={() => onSelectOrganization(org)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-center space-x-2">
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{org.name}</div>
                            <div className="text-xs text-gray-500 truncate">
                              {org.city || org.address || org.contactEmail || "No details"}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-8 text-center">
                      <Building2 className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm font-medium text-gray-500">No organizations found</p>
                      <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Selected Organization Badge */}
            {selectedOrganization && (
              <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                <Building2 className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-900 font-medium">{selectedOrganization.name}</span>
                <button
                  type="button"
                  onClick={() => onSelectOrganization(null)}
                  className="ml-auto text-blue-600 hover:text-blue-800"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Description Field */}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Invoice Description</label>
              <input
                type="text"
                value={invoiceData.description}
                onChange={e => updateInvoiceData("description", e.target.value)}
                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={selectedOrganization ? `Invoice for ${selectedOrganization.name}` : "Enter description"}
              />
            </div>

            {/* Currency Display (Read-only, based on user's country) */}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Currency</label>
              <div className="px-3 py-2 w-full text-sm bg-gray-50 border border-gray-300 rounded-lg text-gray-700">
                {currenciesLoading
                  ? "Loading..."
                  : selectedCurrency
                    ? `${selectedCurrency.symbol || selectedCurrency.code} - ${selectedCurrency.name || selectedCurrency.code}`
                    : `${userCurrencyCode || "KES"}`}
              </div>
              <p className="mt-1 text-xs text-gray-500">Currency is based on your country setting</p>
            </div>
          </div>
        </div>

        {/* Invoice Items */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-base font-medium text-gray-900">Invoice Items</h3>
            <button
              onClick={addItem}
              className="flex justify-center items-center w-8 h-8 text-white rounded-full transition-colors hover:bg-opacity-90"
              style={{ backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR }}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Table Header */}
          <div className="grid grid-cols-12 gap-3 mb-3 text-xs font-medium text-gray-700">
            <div className="col-span-5">Description</div>
            <div className="col-span-2">Unit Price</div>
            <div className="col-span-2">Quantity</div>
            <div className="col-span-2">Amount</div>
            <div className="col-span-1"></div>
          </div>

          {/* Invoice Items */}
          <div className="space-y-2">
            {invoiceItems.map(item => (
              <div key={item.id} className="grid grid-cols-12 gap-3 items-center">
                <div className="col-span-5">
                  <input
                    type="text"
                    value={item.description}
                    onChange={e => updateItem(item.id, "description", e.target.value)}
                    className="px-2 py-2 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Description"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    value={item.unitPrice}
                    onChange={e => updateItem(item.id, "unitPrice", e.target.value)}
                    className="px-2 py-2 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={e => updateItem(item.id, "quantity", e.target.value)}
                    className="px-2 py-2 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    value={item.amount}
                    onChange={e => updateItem(item.id, "amount", e.target.value)}
                    className="px-2 py-2 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
                <div className="col-span-1">
                  <button onClick={() => removeItem(item.id)} className="p-1 text-red-600 hover:text-red-800">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex">
          <button
            onClick={onCreateInvoice}
            disabled={isSubmitting}
            className="flex-1 px-4 py-3 text-sm font-semibold text-white rounded-xl transition-all hover:bg-opacity-90 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR }}
          >
            {isSubmitting ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Processing...
              </>
            ) : (
              "Create & Send Invoice"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Right Preview Component
const InvoicePreview = React.forwardRef(
  ({ invoiceData, invoiceItems, total, onDownloadPDF, selectedOrganization, selectedCurrency }, ref) => {
    const displayItems = invoiceItems || [];
    // Use currency code (KES, GHS) instead of symbol for display
    const currencyDisplay = selectedCurrency?.code || "KES";

    return (
      <div className="overflow-y-auto p-3 w-full max-h-screen bg-white rounded-2xl md:w-1/2 lg:p-4 shadow-xs">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-900">Preview</h2>
          <button
            onClick={onDownloadPDF}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            style={{ color: Constants.HOST_EXPERIENCE_BASE_COLOR }}
            title="Download PDF"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>

        {/* Invoice Preview */}
        <div ref={ref} className="p-6 bg-white border border-gray-200 rounded-lg">
          {/* Header */}
          <div className="mb-6 pb-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Invoice</h1>
            <div className="text-sm text-gray-500">
              Date: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </div>
          </div>

          {/* Client Info */}
          {selectedOrganization && (
            <div className="mb-6">
              <div className="text-xs font-medium text-gray-500 mb-2">BILL TO:</div>
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">{selectedOrganization.name}</div>
                  {selectedOrganization.city && (
                    <div className="text-xs text-gray-500">{selectedOrganization.city}</div>
                  )}
                  {selectedOrganization.contactEmail && (
                    <div className="text-xs text-gray-500">{selectedOrganization.contactEmail}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Invoice Description */}
          <div className="mb-6 p-3 bg-gray-50 rounded-lg">
            <div className="text-xs font-medium text-gray-700 mb-1">Invoice Description:</div>
            <div className="text-sm text-gray-900">
              {invoiceData.description || <span className="text-gray-400 italic">Enter description above</span>}
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-6">
            <div className="text-sm font-semibold text-gray-900 mb-3">Invoice Items</div>
            <div className="grid grid-cols-12 gap-2 py-2 text-xs font-medium text-gray-500 uppercase border-b border-gray-300">
              <div className="col-span-6">Item Description</div>
              <div className="col-span-2 text-center">Qty</div>
              <div className="col-span-2 text-center">Rate</div>
              <div className="col-span-2 text-right">Amount</div>
            </div>

            {displayItems.length > 0 ? (
              displayItems.map(item => (
                <div key={item.id} className="grid grid-cols-12 gap-2 py-2 border-b border-gray-100">
                  <div className="col-span-6 text-sm text-gray-900">{item.description || "Item"}</div>
                  <div className="col-span-2 text-sm text-center text-gray-600">{item.quantity}</div>
                  <div className="col-span-2 text-sm text-center text-gray-600">
                    {currencyDisplay}
                    {item.unitPrice.toFixed(2)}
                  </div>
                  <div className="col-span-2 text-sm text-right text-gray-900">
                    {currencyDisplay}
                    {item.amount.toFixed(2)}
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-gray-400 text-sm">No items added yet</div>
            )}
          </div>

          {/* Total */}
          <div className="flex justify-end mb-4">
            <div className="w-48">
              <div className="flex justify-between py-3 border-t-2 border-gray-300">
                <span className="text-base font-bold text-gray-900">TOTAL</span>
                <span className="text-lg font-bold" style={{ color: Constants.HOST_EXPERIENCE_BASE_COLOR }}>
                  {currencyDisplay}
                  {total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default AddInvoice;
